/* BaseActivityViewComponent.js */

"use strict";
const sanitizeFilename = require("sanitize-filename");
const jsonPatch = require("fast-json-patch");
const jsDiff = require("diff");
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
//const UserGridData = require('@gluenetworks/entity_grid_gl').UserData;

class BaseActivityViewComponent extends EventEmitter {
    constructor(logger, gameEditor, io, utils, userSession, solutionsManager, storageFactory, gridKeyParts) {
        super();
        this._logger = logger;
        this._steveIo = io;
        this._steveUtils = utils;
        this._gameEditor = gameEditor;
        this._gridKeyParts = gridKeyParts || [];
        this._userSession = userSession;
        this._solutionName =
            (solutionsManager.activeSolution && solutionsManager.activeSolution.name) || "";
        this._gridKey = _.camelCase(this._solutionName);
        this._gridKey = this._gridKeyParts.length > 0
            ? this._gridKey + "_" + this._gridKeyParts.join("_")
            : this._gridKey;
        this._gridKey = this._gridKey + "_activityView";
        this._userGridData = storageFactory.getRemoteStorageInstanceForEntityGrid(
            "entity_grid_gl",
            this._gridKey
        );

        this._entity = undefined;
        this._activityFeed = undefined;

        this._renderer = undefined;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        if (this._activityFeed) {
            this._activityFeed.on(
                "EntityAdded",
                (this._onActivityAdded = this._onActivityAdded.bind(this))
            );
            this._activityFeed.on(
                "EntityRemoved",
                (this._onActivityRemoved = this._onActivityRemoved.bind(this))
            );
            this._activityFeed.on(
                "EntityPropertiesUpdated",
                (this._onActivityUpdated = this._onActivityUpdated.bind(this))
            );
        }
    }

    _unregisterListeners() {
        if (this._activityFeed) {
            this._activityFeed.removeListener("EntityAdded", this._onActivityAdded);
            this._activityFeed.removeListener("EntityRemoved", this._onActivityRemoved);
            this._activityFeed.removeListener("EntityPropertiesUpdated", this._onActivityUpdated);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenFeedReady() {
        return this._activityFeed.whenFeedReady();
    }

    wasFeedPreviouslyInitialized() {
        return this._activityFeed.wasPreviouslyInitialized();
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();

        return (
            this._renderer
                .shutdown()
                //.then(()=> {
                //if (!_.isUndefined(this._activityFeed)) {
                //    return this._activityFeed.shutdown();
                //}
                //else {
                //    return Promise.resolve();
                //}
                //})
                .then(
                    () => {
                        this._logger = undefined;
                        this._gameEditor = undefined;
                        this._renderer = undefined;
                        this._steveIo = undefined;
                        this._steveUtils = undefined;
                        this._entity = undefined;
                        this._activityFeed = undefined;
                    },
                    (err) => {
                        this._logger.warn(
                            { error: err },
                            "BaseActivityViewComponent.shutdown() error."
                        );
                    }
                )
        );
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    get entityInstanceId() {
        return this._entityInstanceId;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onUserSorted(sortData) {
        let sort = {};
        let patchedSort;
        sort[sortData.sortColumn] = sortData.sort === "asc" ? 1 : -1;

        patchedSort = this._patchSorting(sort);

        this._activityFeed.setSort(patchedSort);
    }

    _onActivityAdded(event) {
        let activity = {};
        let normalizedActivity = this._normalizeActivity(event);
        if (normalizedActivity) {
            activity[event.id] = normalizedActivity;
            this.emit("ActivityAdded", activity);
        }
    }

    _onActivityUpdated(event) {
        let activity = {};
        activity[event.id] = this._normalizeActivity(event);
        this.emit("ActivityUpdated", activity);
    }

    _onActivityRemoved(event) {
        this.emit("ActivityRemoved", event.id);
    }

    _onFilterChanged(filterObj) {
        this._logger.debug("Filter has changed! Do something with this: ", filterObj);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    getActivityInListById(id) {
        let inst = null;
        if (this._activityFeed) {
            inst = this._activityFeed.getInstanceById(id);
        }
        return inst;
    }

    getActivityList() {
        //let list = {};
        /*
        if (this._activityFeed) {
            Object.keys(this._activityFeed.cache).forEach((activityId) => {
                let activity = this._activityFeed.cache[activityId];
                let normAct = this._normalizeActivity(activity);
                if (normAct) {
                    list[normAct.id] = normAct;
                }
            });
        }*/
        return this._activityFeed.cache;
    }

    isActivityListEmpty() {
        if (this._activityFeed.getNumOfCachedInstances === 0) {
            return true;
        } else {
            return false;
        }
    }

    downloadActivity(activity) {
        if (activity) {
            let activityDate = new Date(
                this._steveUtils.getTimestamp(activity.createdOn)
            ).toISOString();
            let triggerTypeName = sanitizeFilename(`${this._entity.name}`);
            let fileName = triggerTypeName;
            let fileConfig = {};

            let before = {};
            let beforeFilename = `${triggerTypeName}.${activityDate}.before.json`;
            switch (activity.getProperty("activityType")) {
                case "create":
                    fileName += `_create_details_${activityDate}.zip`;
                    break;
                case "update":
                    fileName += `_update_details_${activityDate}.zip`;
                    before = activity.getProperty("before");
                    break;
            }
            let beforeStr = JSON.stringify(before, null, "\t");

            let patch = activity.getProperty("patch");
            let patchFileName = `${triggerTypeName}.${activityDate}.json-patch`;

            let after = _.cloneDeep(before);
            jsonPatch.applyPatch(after, patch);
            let afterStr = JSON.stringify(after, null, "\t");
            let afterFileName = `${triggerTypeName}.${activityDate}.after.json`;

            let max = Math.max(afterStr.length, beforeStr.length);
            let diffStr = jsDiff.createTwoFilesPatch(
                beforeFilename,
                afterFileName,
                beforeStr,
                afterStr,
                null,
                null,
                {
                    context: max
                }
            );
            let diffFileName = `${triggerTypeName}.${activityDate}.diff`;

            fileConfig[beforeFilename] = {
                type: "application/json",
                content: beforeStr
            };
            fileConfig[afterFileName] = {
                type: "application/json",
                content: JSON.stringify(after, null, "\t")
            };
            fileConfig[patchFileName] = {
                type: "application/json-patch+json",
                content: JSON.stringify(patch, null, "\t")
            };
            fileConfig[diffFileName] = {
                type: "text/plain",
                content: diffStr
            };
            if (!_.isEmpty(fileConfig)) {
                this._steveIo.fileFactory.saveStringsAsZip(fileConfig, sanitizeFilename(fileName));
            }
        }
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = BaseActivityViewComponent;

BaseActivityViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.io",
    "$teve.utils",
    "$teve.userSession",
    "$teve.solutionsManager",
    "$teve.storageFactory"
];
